import { Card, Flex, Input, InputNumber, Select, Tag } from "antd";
import _, { set } from "lodash";
import { Dispatch, ReactElement, useEffect, useRef, useState } from "react";
import AdvancedTable, {
  AdvancedTableRef,
  getActionColumns,
} from "../components/AdvancedTable";
import {
  CharactersProvider,
  useCharacters,
} from "../contexts/CharactersProvider";
import { CharacterProfile } from "../shared/types/CharacterProfile";
import { House } from "../shared/types/House";
import { HouseElement } from "../shared/types/HouseElement";
import { WitchGrade } from "../shared/types/WitchGrade";
import { dataCall } from "../tsrpc";

interface HouseElementRecord {
  id: string;
  extra?: any;
}
interface HouseRecord {
  id: string;
  elements: HouseElementRecord[];
}

export default function PageCharacters() {
  return (
    <CharactersProvider>
      <InnerComponent />
    </CharactersProvider>
  );
}

function InnerComponent() {
  const characters = useCharacters();
  const [houses, setHouses] = useState<House[]>([]);
  const [houseElements, setHouseElements] = useState<HouseElement[]>([]);
  const [grades, setGrades] = useState<WitchGrade[]>([]);
  const [gradeOptions, setGradeOptions] = useState<
    { label: number; value: number }[]
  >([]);
  const [levelOptions, setLevelOptions] = useState<
    { label: number; value: number }[]
  >([]);
  const tableRef = useRef<AdvancedTableRef<CharacterProfile>>(null);
  const [editingData, setEditingData] = useState<CharacterProfile>();
  const [searchChaId, setSearchChaId] = useState<string>("");
  const [filter, setFilter] = useState<any>({});

  async function loadData<T>(
    collection: string,
    setter: Dispatch<React.SetStateAction<T[]>>,
  ): Promise<T[]> {
    const res = await dataCall("editor/DataFind", {
      collection,
      limit: 10000,
    });
    setter(res.res?.data ?? []);
    return res.res?.data ?? [];
  }

  async function init() {
    await loadData("houses", setHouses);
    await loadData("houseelements", setHouseElements);
    const grades = await loadData("witchgrades", setGrades);
    setGradeOptions(
      _.uniqBy(grades, "grade").map((g) => ({
        label: g.grade,
        value: g.grade,
      })),
    );
  }

  function getLevelOptions(changed: Partial<CharacterProfile>) {
    if (changed.grade !== undefined)
      setLevelOptions(
        grades
          .filter((g) => g.grade === changed.grade)
          .map((g) => ({ label: g.level, value: g.level })),
      );
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    getLevelOptions({ grade: editingData?.grade ?? 0 });
  }, [grades, editingData]);

  useEffect(() => {
    setFilter(searchChaId ? { _id: { $regex: searchChaId } } : {});
  }, [searchChaId]);

  return (
    <AdvancedTable<CharacterProfile>
      ref={tableRef}
      context={characters}
      filter={filter}
      editingData={editingData}
      onEditingDataChange={getLevelOptions}
      columns={[
        {
          title: () => (
            <Flex align="center">
              <p style={{ flex: 1 }}>角色ID</p>
              <Input.Search
                style={{ flex: 3 }}
                allowClear
                value={searchChaId}
                onClear={() => setSearchChaId("")}
                onChange={(v) => setSearchChaId(v.target.value)}
              />
            </Flex>
          ),
          dataIndex: "_id",
          width: 250,
        },
        {
          title: "账号ID",
          dataIndex: "userId",
          width: 250,
        },
        {
          title: "角色名",
          dataIndex: "name",
          width: 150,
          editor: <Input allowClear maxLength={20} />,
        },
        {
          title: "等级",
          dataIndex: "grade",
          width: 100,
          editor: <Select options={gradeOptions} />,
        },
        {
          title: "等级进度",
          dataIndex: "level",
          width: 100,
          editor: <Select options={levelOptions} />,
        },
        {
          title: "金币",
          dataIndex: "coins",
          width: 150,
          editor: <InputNumber min={0} max={9999} />,
        },
        {
          title: "免广等级",
          dataIndex: "adFreeLevel",
          width: 120,
          render: (value) => value ?? 0,
          editor: <InputNumber min={0} max={100} precision={0} />,
        },
        {
          title: "小屋",
          dataIndex: "houses",
          render: (value: HouseRecord[], record, index) =>
            value.length ? (
              <>
                {value.map((house, houseIndex) => (
                  <Card
                    size="small"
                    key={houseIndex}
                    title={
                      houses.find((h) => h.houseId === house?.id)?.name ??
                      house?.id
                    }
                  >
                    {house.elements.map((element, elementIndex) => (
                      <Tag key={elementIndex}>
                        {houseElements.find((e) => e.assetId === element?.id)
                          ?.name ?? element?.id}
                      </Tag>
                    ))}
                  </Card>
                ))}
              </>
            ) : (
              "暂无数据"
            ),
          editor: <HouseEditor />,
        },
        getActionColumns({
          editingData,
          setEditingData,
          editSubmit: () => tableRef.current?.editSubmit,
          $delete: () => tableRef.current?.delete,
          display: (record) => record?.name,
        }),
      ]}
    />
  );

  function HouseEditor({
    value,
    onChange,
  }: {
    value?: HouseRecord[];
    onChange?: (value: HouseRecord[]) => void;
  }): ReactElement {
    return (
      <>
        <Select
          mode="multiple"
          options={houses.map((h) => ({ label: h.name, value: h.houseId }))}
          value={value?.map((v) => v.id)}
          onChange={(v) => {
            onChange?.(
              v.map(
                (id) => value?.find((h) => h.id === id) ?? { id, elements: [] },
              ),
            );
          }}
        />
        {value?.map((house) => (
          <Card
            key={house.id}
            size="small"
            title={houses.find((h) => h.houseId === house.id)?.name ?? house.id}
          >
            <Select
              mode="multiple"
              options={houseElements
                .filter((he) => he.houseId === house.id)
                .map((he) => ({ label: he.name, value: he.assetId }))}
              value={value
                ?.find((h) => h.id === house.id)
                ?.elements?.map((e) => e.id)}
              onChange={(v) => {
                console.log(v);
                const result = [...(value ?? [{ id: house.id, elements: [] }])];
                result[result.findIndex((r) => r.id === house.id)].elements =
                  v.map((vid) => ({ id: vid }));
                onChange?.(result);
              }}
            />
          </Card>
        ))}
      </>
    );
  }
}
